import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../../components/Input/Input";
import Button from "../../../../components/Button/Button";
import Modal from "../../../../components/Modal/Modal";
import Notification from "../../../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../../../apiService";

/* ===================== STYLES ===================== */
import styles from "./CreateApiProduct.module.scss";

const CreateApiProduct = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();

  // ===============================
  // FAILOVER TOGGLE STATE
  // ===============================
  const [, setEnableFailover] = useState(false);
  const [, setUpdatingFailover] = useState(false);

  // ===============================
  // MODAL & UI STATE
  // ===============================
  const floatRegex = /^\d+(\.\d{1,2})?$/;
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalType, setModalType] = useState("");
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [, setApiDetail] = useState(null);

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  // ===============================
  // DROPDOWN DATA STATE
  // ===============================
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [modalErrors, setModalErrors] = useState({});
  const [failoverApis, setFailoverApis] = useState([]);

  // ===============================
  // MODAL FORM STATE
  // ===============================
  const [modalForm, setModalForm] = useState({
    name: "",
    secretKey: "",
    uatSecretKey: "",
  });

  // ===============================
  // MAIN API FORM STATE
  // ===============================
  const [formData, setFormData] = useState({
    vendorId: "",
    categoryId: "",
    name: "",
    addCredits: "",
    addCost: "",
    introduction: "",
    preProductionUrl: "",
    productionUrl: "",
    methods: "POST",
    level: "",
    filoverApiId: "",
    failoverVendorId: "",
    failoverCategoryId: "",
  });

  const [requestParams, setRequestParams] = useState([
    {
      title: "Api Code",
      name: "api_code",
      vendorKey: "",
      value: "",
      dataType: "String",
      description: "Unique ID for the request from the Client side",
      required: true,
      locked: true,
    },
  ]);

  const [responseParams, setResponseParams] = useState([
    {
      title: "",
      name: "",
      value: "",
      dataType: "",
      description: "",
      required: true,
    },
  ]);

  const [errors, setErrors] = useState([
    {
      name: "",
      response: "",
      description: "",
      value: "",
      vendorKey: "",
      responseTemplate: "",
      responseVendor: "",
      required: true,
    },
  ]);

  const isValidJsonObjectOrArray = (value) => {
    try {
      if (typeof value === "object" && value !== null) return true;
      if (typeof value !== "string" || !value.trim()) return false;
      const parsed = JSON.parse(value);
      return parsed !== null && typeof parsed === "object";
    } catch {
      return false;
    }
  };

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    const e = {};

    if (!formData.level) e.level = "This field is required";
    if (!formData.vendorId) e.vendorId = "This field is required";
    if (!formData.categoryId) e.categoryId = "This field is required";
    if (!formData.name) e.name = "This field is required";

    if (formData.addCredits === "") {
      e.addCredits = "This field is required";
    } else if (!floatRegex.test(formData.addCredits)) {
      e.addCredits = "Enter a valid number (up to 2 decimals)";
    } else if (parseFloat(formData.addCredits) <= 0) {
      e.addCredits = "Credits must be greater than 0";
    }

    if (formData.addCost === "") {
      e.addCost = "This field is required";
    } else if (!floatRegex.test(formData.addCost)) {
      e.addCost = "Enter a valid number (up to 2 decimals)";
    } else if (parseFloat(formData.addCost) <= 0) {
      e.addCost = "Cost must be greater than 0";
    }

    if (formData.level === "L2") {
      if (!formData.filoverApiId) e.filoverApiId = "This field is required";
    }

    if (!formData.introduction) e.introduction = "This field is required";
    if (!formData.productionUrl) e.productionUrl = "This field is required";
    if (!formData.preProductionUrl)
      e.preProductionUrl = "This field is required";
    if (!formData.methods) e.methods = "This field is required";

    requestParams.forEach((p, i) => {
      if (!p.title) e[`req_title_${i}`] = "This field is required";
      if (!p.name) e[`req_name_${i}`] = "This field is required";
      if (!p.vendorKey) e[`req_vendorKey_${i}`] = "This field is required";
      if (!p.dataType) e[`req_type_${i}`] = "This field is required";
      if (!p.description) e[`req_desc_${i}`] = "This field is required";
      if (p.required === "") e[`req_required_${i}`] = "This field is required";
    });

    responseParams.forEach((p, i) => {
      if (!p.name) e[`res_name_${i}`] = "This field is required";
      if (!p.dataType) e[`res_type_${i}`] = "This field is required";
      if (!p.description) e[`res_desc_${i}`] = "This field is required";
    });

    errors.forEach((er, i) => {
      if (!er.title) e[`err_title_${i}`] = "This field is required";
      if (!er.name) e[`err_code_${i}`] = "This field is required";
      if (!er.vendorKey) e[`err_vendorKey_${i}`] = "This field is required";
      if (!er.value) e[`err_value_${i}`] = "This field is required";
      if (!er.description) e[`err_desc_${i}`] = "This field is required";

      if (!er.response) {
        e[`err_response_${i}`] = "This field is required";
      } else if (!isValidJsonObjectOrArray(er.response)) {
        e[`err_response_${i}`] = "Only JSON Object/Array is allowed";
      }

      if (!er.responseTemplate) {
        e[`err_template_${i}`] = "This field is required";
      } else if (!isValidJsonObjectOrArray(er.responseTemplate)) {
        e[`err_template_${i}`] = "Only JSON Object/Array is allowed";
      }

      if (!er.responseVendor) {
        e[`err_vendor_${i}`] = "This field is required";
      } else if (!isValidJsonObjectOrArray(er.responseVendor)) {
        e[`err_vendor_${i}`] = "Only JSON Object/Array is allowed";
      }
    });

    setFieldErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleOpenModal = (type) => {
    setModalType(type);
    setModalTitle(type === "vendor" ? "Create Vendor" : "Create Category");
    setModalForm({ name: "", secretKey: "", uatSecretKey: "" });
    setShowModal(true);
    setModalErrors({});
  };

  const updateModalForm = (key, value) => {
    setModalForm((prev) => ({ ...prev, [key]: value }));
  };

  // List Handlers
  const addRequestParam = () => {
    setRequestParams([
      ...requestParams,
      {
        id: null,
        title: "",
        name: "",
        vendorKey: "",
        value: "",
        dataType: "",
        description: "",
        required: true,
        locked: false,
      },
    ]);
  };
  const removeRequestParam = (index) => {
    const newParams = [...requestParams];
    newParams.splice(index, 1);
    setRequestParams(newParams);
  };

  const addResponseParam = () => {
    setResponseParams([
      ...responseParams,
      {
        id: null,
        name: "",
        value: "",
        dataType: "",
        description: "",
        required: true,
      },
    ]);
  };
  const removeResponseParam = (index) => {
    const newParams = [...responseParams];
    newParams.splice(index, 1);
    setResponseParams(newParams);
  };

  const addError = () => {
    setErrors([
      ...errors,
      {
        id: null,
        title: "",
        name: "",
        vendorKey: "",
        value: "",
        description: "",
        response: "",
        responseTemplate: "",
        responseVendor: "",
        required: true,
      },
    ]);
  };
  const removeError = (index) => {
    const newErrors = [...errors];
    newErrors.splice(index, 1);
    setErrors(newErrors);
  };

  const updateRequestParam = (index, key, value) => {
    const updated = [...requestParams];
    updated[index][key] = value;
    setRequestParams(updated);
  };
  const updateResponseParam = (index, key, value) => {
    const updated = [...responseParams];
    updated[index][key] = value;
    setResponseParams(updated);
  };
  const updateErrorParam = (index, key, value) => {
    const updated = [...errors];
    updated[index][key] = value;
    setErrors(updated);
  };

  const normalizeDetails = (list, type) => {
    return (list || []).map((item) => ({
      ...(Number.isInteger(item.id) ? { id: item.id } : {}),
      type,
      title: item.title || null,
      name: item.name || null,
      vendor_key: item.vendorKey || null,
      value: item.value || null,
      dataType: item.dataType || null,
      required: Boolean(item.required),
      description: item.description || null,
      response:
        typeof item.response === "object"
          ? item.response
          : item.response || null,
      response_template:
        type === "Error" ? item.responseTemplate || null : null,
      response_vendor: type === "Error" ? item.responseVendor || null : null,
    }));
  };

  const handleCreateMaster = async () => {
    const e = {};
    if (!modalForm.name?.trim()) e.name = "This field is required";
    if (modalType === "vendor" && !modalForm.secretKey?.trim())
      e.secretKey = "This field is required";
    if (modalType === "vendor" && !modalForm.uatSecretKey?.trim())
      e.uatSecretKey = "This field is required";

    if (Object.keys(e).length > 0) {
      setModalErrors(e);
      return;
    }

    const payload = {
      name: modalForm.name,
      parent_id: modalType === "vendor" ? 3 : 2,
      user_id: 1,
    };

    if (modalType === "vendor") {
      if (!modalForm.secretKey.trim()) {
        showNotification("Secret key is required", "error");
        return;
      }
      payload.token = modalForm.secretKey;
      payload.uat_token = modalForm.uatSecretKey;
    }

    try {
      const res = await apiPost("catalog", "/master/add", payload);
      showNotification(res?.message || "Created successfully", "success");
      modalType === "vendor" ? fetchVendors() : fetchCategories();
      setShowModal(false);
    } catch (err) {
      showNotification(
        err?.response?.data?.message || "Create failed",
        "error",
      );
    }
  };

  const fetchVendors = async () => {
    setLoadingVendors(true);
    try {
      const res = await apiGet("catalog", "/master/children?parent_id=3");
      if (res?.status === "success") {
        setVendors(res.data.map((v) => ({ label: v.name, value: v.id })));
      }
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await apiGet("catalog", "/master/children?parent_id=2");
      if (res?.status === "success") {
        setCategories(res.data.map((c) => ({ label: c.name, value: c.id })));
      }
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchFailoverApis = async (failoverVendorId, failoverCategoryId) => {
    try {
      const res = await apiGet(
        "catalog",
        `/apimaster/by-vendor-category?vendorId=${failoverVendorId}&categoryId=${failoverCategoryId}`,
      );
      if (res?.success) {
        setFailoverApis(
          res.data.map((api) => ({ label: api.name, value: api.id })),
        );
      } else {
        setFailoverApis([]);
      }
    } catch (err) {
      setFailoverApis([]);
    }
  };

  const fetchApiDetails = async () => {
    if (!id) return;
    try {
      const res = await apiGet("catalog", `/apimaster/try/${id}`);
      if (res?.status === "success") {
        const api = res.data.apis?.[0];
        setApiDetail(api);

        setFormData({
          vendorId: api.vendor_id?.toString() || "",
          categoryId: api.category_id?.toString() || "",
          name: api.name || "",
          addCredits: api.add_credits?.toString() || "",
          addCost: api.add_cost?.toString() || "",
          introduction: api.introduction || "",
          productionUrl: api.production_url || "",
          preProductionUrl: api.pre_production_url || "",
          methods: api.methods || "POST",
          level: api.level || "",
          failoverVendorId: api.failover_vendor_id?.toString() || "",
          failoverCategoryId: api.failover_category_id?.toString() || "",
          filoverApiId: api.failover_api_id?.toString() || "",
        });

        if (api.failover_api_id && api.failover_api) {
          setFailoverApis([
            { label: api.failover_api, value: api.failover_api_id.toString() },
          ]);
        }

        setRequestParams(
          (res.data.request_details || []).map((r) => ({
            id: r.id || null,
            title: r.title || "",
            name: r.name || "",
            vendorKey: r.vendor_key || "",
            value: r.value || "",
            dataType: r.data_type || "",
            description: r.description || "",
            required: r.required === "Yes",
          })),
        );

        setResponseParams(
          (res.data.response_details || []).map((r) => ({
            id: r.id || null,
            name: r.name || "",
            value: r.value || "",
            dataType: r.data_type || "",
            description: r.description || "",
            required: r.required === "Yes",
          })),
        );

        setErrors(
          (res.data.error_details || []).map((e) => ({
            id: e.id || null,
            title: e.title || "",
            name: e.name || "",
            vendorKey: e.vendor_key || "",
            value: e.value || "",
            description: e.description || "",
            response:
              typeof e.response === "string"
                ? e.response
                : JSON.stringify(e.response || {}, null, 2),
            responseTemplate:
              typeof e.response_template === "string"
                ? e.response_template
                : JSON.stringify(e.response_template || {}, null, 2),
            responseVendor:
              typeof e.response_vendor === "string"
                ? e.response_vendor
                : JSON.stringify(e.response_vendor || {}, null, 2),
            required: true,
          })),
        );

        setEnableFailover(api.api_call === "L2");
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchCategories();
    if (isEdit) fetchApiDetails();
  }, [id]);

  useEffect(() => {
    const { failoverVendorId, failoverCategoryId } = formData;
    if (!failoverVendorId || !failoverCategoryId) {
      setFailoverApis([]);
      return;
    }
    fetchFailoverApis(failoverVendorId, failoverCategoryId);
  }, [formData.failoverVendorId, formData.failoverCategoryId]);

  const handleSave = async () => {
    if (!validateForm()) {
      showNotification("Please fill all required fields", "error");
      return;
    }

    const payload = {
      vendorId: Number(formData.vendorId),
      categoryId: Number(formData.categoryId),
      name: formData.name,
      addCredits: parseFloat(formData.addCredits),
      addCost: parseFloat(formData.addCost),
      introduction: formData.introduction,
      productionUrl: formData.productionUrl,
      preProductionUrl: formData.preProductionUrl,
      methods: formData.methods,
      level: formData.level,
      ...(formData.level === "L2"
        ? { filoverApiId: Number(formData.filoverApiId) }
        : { filoverApiId: null }),
      orgId: Number(user?.orgId),
      userId: Number(user?.id),
      request_details: normalizeDetails(requestParams, "Request"),
      response_details: normalizeDetails(responseParams, "Response"),
      error_details: normalizeDetails(errors, "Error"),
    };

    try {
      setLoading(true);
      if (isEdit) {
        payload.id = Number(id);
        await apiPost("catalog", "/apimaster/update", payload);
        showNotification("API Updated Successfully", "success");
      } else {
        await apiPost("catalog", "/apimaster", payload);
        showNotification("API Created Successfully", "success");
      }
      setTimeout(() => navigate("/admin/admin-catalogue"), 1200);
    } catch (err) {
      showNotification(err?.response?.data?.message || "Save failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles["admin-ac-create-api-product"]}>
      {/* NOTIFICATION */}
      {notification && (
        <Notification message={notification} type={notificationType} />
      )}

      {/* HEADER */}
      <div className={styles["admin-ac-create-api-product__header"]}>
        <Link
          to="/admin/admin-catalogue"
          className={styles["admin-ac-create-api-product__back-btn"]}
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </Link>
        <h2 className={styles["admin-ac-create-api-product__title"]}>
          {isEdit ? "Edit API Product" : "Create New API Product"}
        </h2>
      </div>

      {/* MAIN CONFIGURATION GRIDS */}
      <div className={styles["admin-ac-create-api-product__card-grid"]}>
        {/* LEFT COLUMN */}
        <div className={styles["admin-ac-create-api-product__card"]}>
          <div>
            <h3 className={styles["admin-ac-create-api-product__card-title"]}>
              Internal Service Selection
            </h3>
            <p className={styles["admin-ac-create-api-product__card-subtitle"]}>
              Choose the internal service for this API product.
            </p>
          </div>

          <div className={styles["admin-ac-create-api-product__form-group"]}>
            <Input
              label="Select Level"
              type="select"
              placeholder="Select Level"
              value={formData.level}
              onChange={(e) => {
                const level = e.target.value;
                setFormData({
                  ...formData,
                  level,
                  ...(level === "L1" && {
                    failoverVendorId: "",
                    failoverCategoryId: "",
                    filoverApiId: "",
                  }),
                });
                setFieldErrors({
                  ...fieldErrors,
                  level: "",
                  failoverVendorId: "",
                  failoverCategoryId: "",
                  filoverApiId: "",
                });
              }}
              error={fieldErrors.level}
              required
              options={[
                { label: "Level 1", value: "L1" },
                { label: "Level 2", value: "L2" },
              ]}
            />

            <div className={styles["admin-ac-create-api-product__input-row"]}>
              <Input
                label="Select Vendor"
                type="select"
                placeholder={
                  loadingVendors ? "Loading vendors..." : "Select Vendor"
                }
                value={formData.vendorId}
                onChange={(e) =>
                  setFormData({ ...formData, vendorId: e.target.value })
                }
                required
                options={vendors}
              />
              {!isEdit && (
                <button
                  type="button"
                  className={`${styles["admin-ac-create-api-product__btn"]} ${styles["admin-ac-create-api-product__btn--secondary"]}`}
                  onClick={() => handleOpenModal("vendor")}
                >
                  <Plus size={16} /> Create
                </button>
              )}
            </div>

            <div className={styles["admin-ac-create-api-product__input-row"]}>
              <Input
                label="Select Category"
                type="select"
                placeholder={
                  loadingCategories
                    ? "Loading categories..."
                    : "Select Category"
                }
                value={formData.categoryId}
                onChange={(e) =>
                  setFormData({ ...formData, categoryId: e.target.value })
                }
                required
                options={categories}
              />
              {!isEdit && (
                <button
                  type="button"
                  className={`${styles["admin-ac-create-api-product__btn"]} ${styles["admin-ac-create-api-product__btn--secondary"]}`}
                  onClick={() => handleOpenModal("category")}
                >
                  <Plus size={16} /> Create
                </button>
              )}
            </div>

            <Input
              label="Add API Name"
              placeholder="Enter API Name"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                setFieldErrors({ ...fieldErrors, name: "" });
              }}
              error={fieldErrors.name}
              required
            />

            <Input
              label="Add Credits"
              placeholder="Enter Credits"
              type="number"
              step="0.01"
              value={formData.addCredits}
              onChange={(e) => {
                setFormData({ ...formData, addCredits: e.target.value });
                setFieldErrors({ ...fieldErrors, addCredits: "" });
              }}
              error={fieldErrors.addCredits}
              required
            />

            <Input
              label="Add Cost"
              placeholder="Enter Cost"
              type="number"
              step="0.01"
              value={formData.addCost}
              onChange={(e) => {
                setFormData({ ...formData, addCost: e.target.value });
                setFieldErrors({ ...fieldErrors, addCost: "" });
              }}
              error={fieldErrors.addCost}
              required
            />

            <Input
              type="textarea"
              label="Introduction"
              placeholder="Enter Introduction"
              value={formData.introduction}
              onChange={(e) => {
                setFormData({ ...formData, introduction: e.target.value });
                setFieldErrors({ ...fieldErrors, introduction: "" });
              }}
              error={fieldErrors.introduction}
              required
            />
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div className={styles["admin-ac-create-api-product__card"]}>
            <div>
              <h3 className={styles["admin-ac-create-api-product__card-title"]}>
                Public Endpoint Configuration
              </h3>
              <p
                className={styles["admin-ac-create-api-product__card-subtitle"]}
              >
                Set up the public endpoint for customer access.
              </p>
            </div>

            <div className={styles["admin-ac-create-api-product__form-group"]}>
              <Input
                label="Production Endpoint Path"
                placeholder="Enter Prod URL"
                value={formData.productionUrl}
                onChange={(e) => {
                  setFormData({ ...formData, productionUrl: e.target.value });
                  setFieldErrors({ ...fieldErrors, productionUrl: "" });
                }}
                error={fieldErrors.productionUrl}
                required
              />

              <Input
                label="Pre Production Endpoint Path"
                placeholder="Enter Pre Prod URL"
                value={formData.preProductionUrl}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    preProductionUrl: e.target.value,
                  });
                  setFieldErrors({ ...fieldErrors, preProductionUrl: "" });
                }}
                error={fieldErrors.preProductionUrl}
                required
              />

              <Input
                label="Methods"
                type="select"
                placeholder="Select Method"
                value={formData.methods}
                onChange={(e) => {
                  setFormData({ ...formData, methods: e.target.value });
                  setFieldErrors({ ...fieldErrors, methods: "" });
                }}
                error={fieldErrors.methods}
                required
                options={[
                  { label: "GET", value: "GET" },
                  { label: "POST", value: "POST" },
                  { label: "PUT", value: "PUT" },
                  { label: "PATCH", value: "PATCH" },
                ]}
              />
            </div>
          </div>

          {formData.level === "L2" && (
            <div className={styles["admin-ac-create-api-product__card"]}>
              <h3 className={styles["admin-ac-create-api-product__card-title"]}>
                Failover Service Selection
              </h3>

              <div
                className={styles["admin-ac-create-api-product__form-group"]}
              >
                <Input
                  label="Select Vendor"
                  type="select"
                  value={formData.failoverVendorId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      failoverVendorId: e.target.value,
                    })
                  }
                  options={vendors}
                />
                <Input
                  label="Select Category"
                  type="select"
                  value={formData.failoverCategoryId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      failoverCategoryId: e.target.value,
                    })
                  }
                  options={categories}
                />
                <Input
                  label="Select APIs"
                  type="select"
                  value={formData.filoverApiId}
                  onChange={(e) =>
                    setFormData({ ...formData, filoverApiId: e.target.value })
                  }
                  options={failoverApis}
                  required
                  error={fieldErrors.filoverApiId}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DYNAMIC LISTS: API CONFIGURATIONS */}
      <div className={styles["admin-ac-create-api-product__card"]}>
        <div>
          <h2
            className={styles["admin-ac-create-api-product__card-title"]}
            style={{ fontSize: "22px" }}
          >
            API Configurations
          </h2>
          <p
            className={styles["admin-ac-create-api-product__card-subtitle"]}
            style={{ marginTop: "4px" }}
          >
            Define the request, response, and error parameters for your API.
          </p>
        </div>

        {/* 1. Request Body Parameters */}
        <div className={styles["admin-ac-create-api-product__form-group"]}>
          <h3 className={styles["admin-ac-create-api-product__card-title"]}>
            Request Body Parameters
          </h3>
          <div className={styles["admin-ac-create-api-product__dynamic-list"]}>
            {requestParams.map((param, index) => (
              <div
                key={index}
                className={styles["admin-ac-create-api-product__dynamic-row"]}
              >
                <Input
                  label="Title"
                  placeholder="Title"
                  value={param.title}
                  onChange={(e) => {
                    updateRequestParam(index, "title", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`req_title_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`req_title_${index}`]}
                  required
                  disabled={param.locked}
                />
                <Input
                  label="Name"
                  placeholder="Name"
                  value={param.name}
                  onChange={(e) => {
                    updateRequestParam(index, "name", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`req_name_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`req_name_${index}`]}
                  required
                  disabled={param.locked}
                />
                <Input
                  label="Vendor Key"
                  placeholder="Vendor Key"
                  value={param.vendorKey}
                  onChange={(e) => {
                    updateRequestParam(index, "vendorKey", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`req_vendorKey_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`req_vendorKey_${index}`]}
                  required
                />
                <Input
                  label="Value"
                  placeholder="Value"
                  value={param.value}
                  onChange={(e) => {
                    updateRequestParam(index, "value", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`req_value_${index}`]: "",
                    });
                  }}
                />
                <Input
                  label="Data Type"
                  type="select"
                  placeholder="Select"
                  value={param.dataType}
                  onChange={(e) => {
                    updateRequestParam(index, "dataType", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`req_type_${index}`]: "",
                    });
                  }}
                  options={[
                    { label: "FileBase64", value: "FileBase64" },
                    { label: "SingleFile", value: "SingleFile" },
                    { label: "MultipleFiles", value: "MultipleFiles" },
                    { label: "String", value: "String" },
                  ]}
                  error={fieldErrors[`req_type_${index}`]}
                  required
                  disabled={param.locked}
                />
                <Input
                  label="Required"
                  type="select"
                  placeholder="Select"
                  value={param.required ? "Yes" : "No"}
                  onChange={(e) => {
                    updateRequestParam(
                      index,
                      "required",
                      e.target.value === "Yes",
                    );
                    setFieldErrors({
                      ...fieldErrors,
                      [`req_required_${index}`]: "",
                    });
                  }}
                  options={[
                    { label: "Yes", value: "Yes" },
                    { label: "No", value: "No" },
                  ]}
                  error={fieldErrors[`req_required_${index}`]}
                  required
                  disabled={param.locked}
                />
                <Input
                  label="Description"
                  placeholder="Description"
                  value={param.description}
                  onChange={(e) => {
                    updateRequestParam(index, "description", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`req_desc_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`req_desc_${index}`]}
                  required
                  disabled={param.locked}
                />
              </div>
            ))}
          </div>
          <div className={styles["admin-ac-create-api-product__list-actions"]}>
            <button
              type="button"
              className={`${styles["admin-ac-create-api-product__btn"]} ${styles["admin-ac-create-api-product__btn--secondary"]}`}
              onClick={addRequestParam}
            >
              <Plus size={16} /> Add Parameter
            </button>
            {requestParams.length > 1 && (
              <button
                type="button"
                className={`${styles["admin-ac-create-api-product__btn"]} ${styles["admin-ac-create-api-product__btn--danger"]}`}
                onClick={() => removeRequestParam(requestParams.length - 1)}
              >
                <Trash2 size={16} /> Remove
              </button>
            )}
          </div>
        </div>

        <hr
          style={{
            border: "none",
            borderTop: "1px solid #e2e8f0",
            margin: "12px 0",
          }}
        />

        {/* 2. Response Body Parameters */}
        <div className={styles["admin-ac-create-api-product__form-group"]}>
          <h3 className={styles["admin-ac-create-api-product__card-title"]}>
            Response Body Parameters
          </h3>
          <div className={styles["admin-ac-create-api-product__dynamic-list"]}>
            {responseParams.map((param, index) => (
              <div
                key={index}
                className={styles["admin-ac-create-api-product__dynamic-row"]}
              >
                <Input
                  label="Name"
                  placeholder="Parameter Name"
                  value={param.name}
                  onChange={(e) => {
                    updateResponseParam(index, "name", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`res_name_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`res_name_${index}`]}
                  required
                />
                <Input
                  label="Value"
                  placeholder="Value"
                  value={param.value}
                  onChange={(e) => {
                    updateResponseParam(index, "value", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`res_value_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`res_value_${index}`]}
                />
                <Input
                  label="Data Type"
                  type="select"
                  placeholder="Select"
                  value={param.dataType}
                  onChange={(e) => {
                    updateResponseParam(index, "dataType", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`res_type_${index}`]: "",
                    });
                  }}
                  options={[
                    { label: "Object", value: "Object" },
                    { label: "String", value: "String" },
                  ]}
                  error={fieldErrors[`res_type_${index}`]}
                  required
                />
                <Input
                  label="Description"
                  placeholder="Description"
                  value={param.description}
                  onChange={(e) => {
                    updateResponseParam(index, "description", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`res_desc_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`res_desc_${index}`]}
                  required
                />
              </div>
            ))}
          </div>
          <div className={styles["admin-ac-create-api-product__list-actions"]}>
            <button
              type="button"
              className={`${styles["admin-ac-create-api-product__btn"]} ${styles["admin-ac-create-api-product__btn--secondary"]}`}
              onClick={addResponseParam}
            >
              <Plus size={16} /> Add Parameter
            </button>
            {responseParams.length > 1 && (
              <button
                type="button"
                className={`${styles["admin-ac-create-api-product__btn"]} ${styles["admin-ac-create-api-product__btn--danger"]}`}
                onClick={() => removeResponseParam(responseParams.length - 1)}
              >
                <Trash2 size={16} /> Remove
              </button>
            )}
          </div>
        </div>

        <hr
          style={{
            border: "none",
            borderTop: "1px solid #e2e8f0",
            margin: "12px 0",
          }}
        />

        {/* 3. Error Handling */}
        <div className={styles["admin-ac-create-api-product__form-group"]}>
          <h3 className={styles["admin-ac-create-api-product__card-title"]}>
            Error Handling
          </h3>
          <div className={styles["admin-ac-create-api-product__dynamic-list"]}>
            {errors.map((err, index) => (
              <div
                key={index}
                className={styles["admin-ac-create-api-product__dynamic-row"]}
              >
                <Input
                  label="Title"
                  type="select"
                  placeholder="Select"
                  value={err.title}
                  onChange={(e) => {
                    updateErrorParam(index, "title", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`err_title_${index}`]: "",
                    });
                  }}
                  options={[
                    { label: "Success", value: "Success" },
                    { label: "Failed", value: "Failed" },
                  ]}
                  error={fieldErrors[`err_title_${index}`]}
                  required
                />
                <Input
                  label="Code"
                  placeholder="Code"
                  value={err.name}
                  onChange={(e) => {
                    updateErrorParam(index, "name", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`err_code_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`err_code_${index}`]}
                  required
                />
                <Input
                  label="Vendor Key"
                  placeholder="Vendor Key"
                  value={err.vendorKey}
                  onChange={(e) => {
                    updateErrorParam(index, "vendorKey", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`err_vendorKey_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`err_vendorKey_${index}`]}
                  required
                />
                <Input
                  label="Value"
                  placeholder="Value"
                  value={err.value}
                  onChange={(e) => {
                    updateErrorParam(index, "value", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`err_value_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`err_value_${index}`]}
                  required
                />
                <Input
                  label="Vendor (JSON)"
                  placeholder='{"http_response_code":404}'
                  value={
                    typeof err.responseVendor === "string"
                      ? err.responseVendor
                      : JSON.stringify(err.responseVendor, null, 2)
                  }
                  onChange={(e) => {
                    updateErrorParam(index, "responseVendor", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`err_vendor_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`err_vendor_${index}`]}
                  required
                />
                <Input
                  label="Doql (JSON)"
                  placeholder='{"error":"Not Found"}'
                  value={
                    typeof err.response === "string"
                      ? err.response
                      : JSON.stringify(err.response, null, 2)
                  }
                  onChange={(e) => {
                    updateErrorParam(index, "response", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`err_response_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`err_response_${index}`]}
                  required
                />
                <Input
                  label="Template (JSON)"
                  placeholder='{"error":"Pan not found"}'
                  value={
                    typeof err.responseTemplate === "string"
                      ? err.responseTemplate
                      : JSON.stringify(err.responseTemplate, null, 2)
                  }
                  onChange={(e) => {
                    updateErrorParam(index, "responseTemplate", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`err_template_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`err_template_${index}`]}
                  required
                />
                <Input
                  label="Description"
                  placeholder="Error description"
                  value={err.description}
                  onChange={(e) => {
                    updateErrorParam(index, "description", e.target.value);
                    setFieldErrors({
                      ...fieldErrors,
                      [`err_desc_${index}`]: "",
                    });
                  }}
                  error={fieldErrors[`err_desc_${index}`]}
                  required
                />
              </div>
            ))}
          </div>
          <div className={styles["admin-ac-create-api-product__list-actions"]}>
            <button
              type="button"
              className={`${styles["admin-ac-create-api-product__btn"]} ${styles["admin-ac-create-api-product__btn--secondary"]}`}
              onClick={addError}
            >
              <Plus size={16} /> Add Error
            </button>
            {errors.length > 1 && (
              <button
                type="button"
                className={`${styles["admin-ac-create-api-product__btn"]} ${styles["admin-ac-create-api-product__btn--danger"]}`}
                onClick={() => removeError(errors.length - 1)}
              >
                <Trash2 size={16} /> Remove
              </button>
            )}
          </div>
        </div>
      </div>

      {/* SAVE BUTTON FOOTER */}
      <div className={styles["admin-ac-create-api-product__save-footer"]}>
        <button
          className={`${styles["admin-ac-create-api-product__btn"]} ${styles["admin-ac-create-api-product__btn--primary"]}`}
          onClick={handleSave}
          disabled={loading}
        >
          <Save size={18} strokeWidth={2.5} />
          {isEdit ? "Update Configuration" : "Save Configuration"}
        </button>
      </div>

      {/* MODAL FOR VENDOR/CATEGORY */}
      {showModal && (
        <Modal title={modalTitle} onClose={() => setShowModal(false)}>
          <div className={styles["admin-ac-create-api-product__modal-content"]}>
            <Input
              label={modalType === "vendor" ? "Vendor Name" : "Category Name"}
              placeholder={
                modalType === "vendor"
                  ? "Enter Vendor Name"
                  : "Enter Category Name"
              }
              value={modalForm.name}
              onChange={(e) => {
                updateModalForm("name", e.target.value);
                setModalErrors({ ...modalErrors, name: "" });
              }}
              error={modalErrors.name}
              required
            />
            {modalType === "vendor" && (
              <Input
                label="Production Secret Key"
                placeholder="Enter Production Secret Key"
                value={modalForm.secretKey}
                onChange={(e) => {
                  updateModalForm("secretKey", e.target.value);
                  setModalErrors({ ...modalErrors, secretKey: "" });
                }}
                error={modalErrors.secretKey}
                required
              />
            )}
            {modalType === "vendor" && (
              <Input
                label="Pre Production Secret Key"
                placeholder="Pre Production Secret Key"
                value={modalForm.uatSecretKey}
                onChange={(e) => {
                  updateModalForm("uatSecretKey", e.target.value);
                  setModalErrors({ ...modalErrors, uatSecretKey: "" });
                }}
                error={modalErrors.uatSecretKey}
                required
              />
            )}

            <div
              className={styles["admin-ac-create-api-product__modal-actions"]}
            >
              <button
                type="button"
                className={`${styles["admin-ac-create-api-product__btn"]} ${styles["admin-ac-create-api-product__btn--secondary"]}`}
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${styles["admin-ac-create-api-product__btn"]} ${styles["admin-ac-create-api-product__btn--primary"]}`}
                onClick={handleCreateMaster}
              >
                Create
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CreateApiProduct;
